/* views/Dashboard.jsx — Home dashboard v7
 * 3 primary round-trips: dashboard_summary, orders_for_date(today), orders_for_date(tomorrow)
 * Parallel: contacts (top customers), inventory (low-stock), unlinked batches
 */

import React from 'react'
import { humanize, formatMoney, formatDate } from '../utils.jsx'
import { rpc, Contacts, Inventory, Production } from '../db.jsx'
import { Pill, Spinner, EmptyState, Sparkline, GroupedBarChart, SimpleBarChart, Donut, AreaLineChart } from '../components.jsx'
import { I } from '../icons.jsx'
import { useAuth } from '../auth.jsx'
import { OrderDrawer } from './Orders.jsx'

const UTENSILS_CATEGORY_ID = "e5e8b293-a081-4f82-9a85-0b5a8234e8cb";

function toISODate(d) {
  return d.toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
}

function Dashboard({ goto }) {
  const { profile } = useAuth();
  const [summary, setSummary]             = React.useState(null);
  const [todayOrders, setTodayOrders]     = React.useState(null);
  const [tomorrowOrders, setTomorrowOrders] = React.useState(null);
  const [contacts, setContacts]           = React.useState(null);
  const [lowStock, setLowStock]           = React.useState(null);
  const [unlinked, setUnlinked]           = React.useState(null);
  const [openOrder, setOpenOrder]         = React.useState(null);

  const todayStr    = toISODate(new Date());
  const tomorrowStr = toISODate(new Date(Date.now() + 86400000));

  const loadBoards = React.useCallback(() => {
    rpc("orders_for_date", { p_date: todayStr })
      .then(r => setTodayOrders(Array.isArray(r) ? r : (r?.data || [])));
    rpc("orders_for_date", { p_date: tomorrowStr })
      .then(r => setTomorrowOrders(Array.isArray(r) ? r : (r?.data || [])));
  }, [todayStr, tomorrowStr]);

  React.useEffect(() => {
    const ctrl = new AbortController();
    const sig = ctrl.signal;

    rpc("dashboard_summary", {}, sig)
      .then(r => { if (!sig.aborted) setSummary(r?.data ?? r); });
    loadBoards();
    Contacts.list(sig)
      .then(r => { if (!sig.aborted) setContacts(Array.isArray(r) ? r : (r?.data || [])); });
    Inventory.current(sig)
      .then(r => { if (!sig.aborted) setLowStock(Array.isArray(r) ? r : (r?.data || [])); });
    Production.unlinked(sig)
      .then(r => { if (!sig.aborted) setUnlinked(Array.isArray(r) ? r : (r?.data || [])); });

    const tick = setInterval(() => { if (!document.hidden) loadBoards(); }, 60000);
    return () => { ctrl.abort(); clearInterval(tick); };
  }, [loadBoards]);

  const s = summary || {};

  const statusColors = {
    preparing: "var(--cyan)", paid: "#a855f7", out_for_delivery: "var(--pink)",
    completed: "var(--ok)", delivered: "var(--ok)", pending: "var(--warn)",
    cancelled: "var(--text-faint)",
  };
  const donutData = (s.status_counts || []).map(x => ({
    label: humanize(x.status), value: x.count,
    color: statusColors[x.status] || "var(--info)",
  }));

  const monthRevenue = s.monthly_data || [];
  const topProducts  = s.top_products_30d || [];

  const topContacts = (contacts || [])
    .slice().sort((a, b) => (b.total_spent || 0) - (a.total_spent || 0))
    .slice(0, 5);

  const lowStockItems = (lowStock || [])
    .filter(i => (i.qty_remaining || 0) < 5 && i.category_id !== UTENSILS_CATEGORY_ID)
    .slice(0, 5);

  const weekDelta      = (s.orders_this_week || 0) - (s.orders_last_week || 0);
  const weekRevDelta   = (s.revenue_this_week || 0) - (s.revenue_last_week || 0);
  const firstName      = profile?.full_name?.split(' ')[0] || profile?.email?.split('@')[0] || 'there';

  return (
    <div>
      {/* Welcome */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
          Welcome back, {firstName}
        </h2>
      </div>

      {/* Today + Tomorrow boards */}
      <div className="grid c2" style={{ marginBottom: 14 }}>
        <DayBoard title="Today" date={todayStr} orders={todayOrders}
                  onOpenOrder={setOpenOrder} />
        <DayBoard title="Tomorrow" date={tomorrowStr} orders={tomorrowOrders}
                  onOpenOrder={setOpenOrder} />
      </div>

      {/* Row 1 — operational KPIs */}
      <div className="grid c4">
        <Kpi icon={I.money} label="Lifetime Revenue" tone="cyan"
             value={summary ? formatMoney(s.lifetime_revenue) : "…"}
             foot="From orders, excl. cancelled"
             spark={monthRevenue.map(m => m.Revenue || 0)} sparkColor="var(--cyan)" />
        <Kpi icon={I.clock} label="Open Orders" tone="cream"
             value={summary ? String(s.open_orders || 0) : "…"}
             foot="pending → out for delivery" />
        <Kpi icon={I.warn} label="Low-stock Items" tone="danger"
             value={summary ? String(s.low_stock_count || 0) : "…"}
             foot="excl. utensils & equipment" />
        <Kpi icon={I.spark} label="Unpaid Balance" tone="pink"
             value={summary ? formatMoney(s.unpaid_balance_total) : "…"}
             foot="money owed across open orders"
             spark={monthRevenue.map(m => m.Revenue || 0)} sparkColor="var(--pink)" />
      </div>

      {/* Row 2 — performance KPIs */}
      <div className="grid c4" style={{ marginTop: 14 }}>
        <PerfKpi label="Avg order value (30d)"
                 value={summary ? formatMoney(s.avg_order_value_30d) : "…"}
                 foot={summary ? `${s.last_30_count} orders` : "loading…"} />
        <PerfKpi label="Orders this week"
                 value={summary ? String(s.orders_this_week || 0) : "…"}
                 foot={summary
                   ? `${weekDelta >= 0 ? "+" : ""}${weekDelta} vs last week (${s.orders_last_week || 0})`
                   : "loading…"}
                 delta={summary ? weekDelta : null} />
        <PerfKpi label="Reorder rate (90d)"
                 value={summary ? `${s.reorder_rate_90d ?? 0}%` : "…"}
                 foot="customers with ≥2 orders" />
        <PerfKpi label="Production yield (30d)"
                 value={summary ? `${s.production_yield_30d ?? 0}%` : "…"}
                 foot="batches linked to an order" />
      </div>

      {/* Row 3 — financial KPIs */}
      <div className="grid c4" style={{ marginTop: 14 }}>
        <FinKpi label="Gross Profit"   value={summary ? formatMoney(s.gross_profit) : "…"}
                foot="revenue − COGS"
                tone={summary ? (s.gross_profit >= 0 ? "ok" : "danger") : "muted"} />
        <FinKpi label="Total Expenses" value={summary ? formatMoney(s.total_expenses) : "…"}
                foot="business items only" tone="muted" />
        <FinKpi label="Net Profit"     value={summary ? formatMoney(s.net_profit) : "…"}
                foot="gross profit − expenses"
                tone={summary ? (s.net_profit >= 0 ? "ok" : "danger") : "muted"} />
        <FinKpi label="Inventory value" value={summary ? formatMoney(s.inventory_value) : "…"}
                foot="stock on hand (excl. utensils)" tone="muted" />
      </div>

      {/* Row 4 — charts */}
      <div className="grid" style={{ marginTop: 14, gridTemplateColumns: "1.4fr 1fr" }}>
        <div className="card">
          <div className="card-head">
            <div><h3>Revenue — last 6 months</h3><div className="sub">From orders, excl. cancelled</div></div>
          </div>
          <div className="card-pad" style={{ paddingTop: 4 }}>
            {summary
              ? <AreaLineChart data={monthRevenue} />
              : <Spinner label="Loading chart…" />}
          </div>
        </div>
        <div className="card">
          <div className="card-head">
            <div><h3>Orders by status</h3><div className="sub">All-time distribution</div></div>
          </div>
          <div className="card-pad" style={{ paddingTop: 4 }}>
            {summary
              ? donutData.length > 0
                ? <Donut data={donutData} height={200} />
                : <EmptyState title="No orders yet" />
              : <Spinner label="Loading chart…" />}
          </div>
        </div>
      </div>

      <div className="grid c2" style={{ marginTop: 14 }}>
        <div className="card">
          <div className="card-head">
            <div><h3>Revenue vs COGS — last 6 months</h3><div className="sub">Grouped by month</div></div>
            <div className="legend">
              <span className="item"><span className="swatch" style={{ background: "var(--cyan)" }}></span>Revenue</span>
              <span className="item"><span className="swatch" style={{ background: "var(--pink)" }}></span>COGS</span>
            </div>
          </div>
          <div className="card-pad" style={{ paddingTop: 4 }}>
            {summary
              ? <GroupedBarChart data={monthRevenue} keys={["Revenue", "COGS"]} colors={["#4fc8e0", "#ec4899"]} />
              : <Spinner label="Loading chart…" />}
          </div>
        </div>
        <div className="card">
          <div className="card-head">
            <div><h3>Top products — last 30 days</h3><div className="sub">By units sold</div></div>
          </div>
          <div className="card-pad" style={{ paddingTop: 4 }}>
            {summary
              ? topProducts.length > 0
                ? <SimpleBarChart data={topProducts} suffix=" units" />
                : <EmptyState title="No order data yet" />
              : <Spinner label="Loading chart…" />}
          </div>
        </div>
      </div>

      {/* Row 5 — top customers + low stock */}
      <div className="grid c2" style={{ marginTop: 14 }}>
        <div className="card">
          <div className="card-head">
            <div><h3>Top customers</h3><div className="sub">By lifetime spend</div></div>
            <button className="btn btn-ghost btn-sm" onClick={() => goto("contacts")}>View all</button>
          </div>
          <div className="tbl-wrap">
            {contacts === null
              ? <Spinner label="Loading…" />
              : <table className="tbl">
                  <thead><tr><th>Name</th><th>Type</th><th>Orders</th><th style={{ textAlign: "right" }}>Total spent</th></tr></thead>
                  <tbody>
                    {topContacts.length === 0 && (
                      <tr><td colSpan={4} style={{ textAlign: "center", color: "var(--text-faint)", padding: 24 }}>No contacts yet</td></tr>
                    )}
                    {topContacts.map(c => (
                      <tr key={c.id}>
                        <td style={{ fontWeight: 600 }}>{c.name}</td>
                        <td><Pill tone={c.type === "reseller" ? "cyan" : c.type === "wholesale" || c.type === "restaurant" ? "pink" : "muted"}>{humanize(c.type)}</Pill></td>
                        <td className="mono">{c.total_orders || 0}</td>
                        <td className="amt" style={{ textAlign: "right" }}>{formatMoney(c.total_spent || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>}
          </div>
        </div>
        <div className="card">
          <div className="card-head">
            <div><h3>Low-stock alerts</h3><div className="sub">Business categories only</div></div>
            <button className="btn btn-ghost btn-sm" onClick={() => goto("inventory")}>Go to inventory</button>
          </div>
          <div className="tbl-wrap">
            {lowStock === null
              ? <Spinner label="Loading…" />
              : <table className="tbl">
                  <thead><tr><th>Item</th><th>Category</th><th>Remaining</th><th>Status</th></tr></thead>
                  <tbody>
                    {lowStockItems.length === 0 && (
                      <tr><td colSpan={4} style={{ textAlign: "center", color: "var(--text-faint)", padding: 24 }}>All stocked up ✓</td></tr>
                    )}
                    {lowStockItems.map(i => (
                      <tr key={i.item_id}>
                        <td style={{ fontWeight: 600 }}>{i.item_name}</td>
                        <td className="muted">{i.category_name}</td>
                        <td className="mono">{i.qty_remaining} <span className="faint mono" style={{ fontSize: 11 }}>{i.unit}</span></td>
                        <td><Pill tone="danger"><span className="dot"/>Low</Pill></td>
                      </tr>
                    ))}
                  </tbody>
                </table>}
          </div>
        </div>
      </div>

      {/* Row 6 — unlinked batches */}
      {unlinked !== null && unlinked.length > 0 && (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="card-head">
            <div><h3>Unlinked batches</h3><div className="sub">Pending batches with no order assigned</div></div>
            <button className="btn btn-ghost btn-sm" onClick={() => goto("production")}>Go to production</button>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>Product</th><th>Date made</th><th>Qty</th></tr></thead>
              <tbody>
                {unlinked.map(b => (
                  <tr key={b.id}>
                    <td style={{ fontWeight: 600 }}>{b.product_name_snapshot}</td>
                    <td className="muted">{formatDate(b.date_made)}</td>
                    <td className="mono">{b.qty_produced} <span className="faint" style={{ fontSize: 11 }}>{b.unit}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openOrder && (
        <OrderDrawer
          order={openOrder}
          onClose={() => setOpenOrder(null)}
          onChanged={() => { setOpenOrder(null); loadBoards(); }}
        />
      )}
    </div>
  );
}

/* ---------- Today / Tomorrow board --------------------------------------- */
function DayBoard({ title, date, orders, onOpenOrder }) {
  const today = toISODate(new Date());
  const isToday = date === today;
  const count = orders ? orders.length : null;

  return (
    <div className="card">
      <div className="card-head">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          {count !== null && (
            <span style={{
              background: count > 0 ? "var(--cyan)" : "var(--surface-3)",
              color: count > 0 ? "#fff" : "var(--text-faint)",
              borderRadius: 99, fontSize: 11, fontWeight: 700,
              padding: "1px 8px", minWidth: 20, textAlign: "center",
            }}>{count}</span>
          )}
        </div>
        <span className="muted" style={{ fontSize: 12 }}>{formatDate(date)}</span>
      </div>
      <div className="tbl-wrap">
        {orders === null
          ? <div style={{ padding: 20, textAlign: "center" }}><Spinner label="Loading…" /></div>
          : orders.length === 0
            ? <div style={{ padding: "20px 16px", textAlign: "center", color: "var(--text-faint)", fontSize: 13 }}>
                {isToday ? "No orders today" : "No orders tomorrow"}
              </div>
            : <table className="tbl">
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id} style={{ cursor: "pointer" }} onClick={() => onOpenOrder(o)}>
                      <td style={{ width: 70, color: "var(--text-faint)", fontSize: 12 }}>
                        {o.meetup_datetime
                          ? new Date(o.meetup_datetime).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", hour12: true })
                          : "—"}
                      </td>
                      <td style={{ fontWeight: 600, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {o.contact_name || "—"}
                      </td>
                      <td>
                        <Pill tone={statusTone(o.status)} style={{ fontSize: 11 }}>
                          {humanize(o.status)}
                        </Pill>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                          {o.balance_due > 0 && (
                            <span style={{
                              width: 7, height: 7, borderRadius: "50%",
                              background: "var(--danger)", display: "inline-block", flexShrink: 0,
                            }} title={`Balance due: ${formatMoney(o.balance_due)}`} />
                          )}
                          <span className="mono" style={{ fontSize: 12 }}>{formatMoney(o.total)}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>}
      </div>
    </div>
  );
}

function statusTone(status) {
  const m = { pending: "warn", preparing: "cyan", paid: "pink", out_for_delivery: "pink",
              completed: "ok", delivered: "ok", cancelled: "muted", ready: "cyan" };
  return m[status] || "muted";
}

/* ---------- KPI tiles ---------------------------------------------------- */
function Kpi({ icon, label, value, foot, tone, spark, sparkColor }) {
  return (
    <div className="kpi">
      <div className="kpi-head">
        <span>{label}</span>
        <span className="kpi-ico" style={{ color: `var(--${tone === "cream" ? "cream" : tone === "danger" ? "danger" : tone === "pink" ? "pink" : "cyan"})` }}>{icon}</span>
      </div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-foot">{foot}</div>
      {spark && <Sparkline data={spark} color={sparkColor || "var(--cyan)"} />}
    </div>
  );
}

function PerfKpi({ label, value, foot, delta }) {
  const deltaColor = delta == null ? undefined
    : delta > 0 ? "var(--ok)" : delta < 0 ? "var(--danger)" : "var(--text-faint)";
  return (
    <div className="kpi">
      <div className="kpi-head"><span>{label}</span></div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-foot" style={delta != null ? { color: deltaColor } : undefined}>{foot}</div>
    </div>
  );
}

function FinKpi({ label, value, foot, tone }) {
  const colors = { ok: "var(--ok)", danger: "var(--danger)", warn: "var(--warn)", muted: "var(--text)" };
  return (
    <div className="kpi">
      <div className="kpi-head"><span>{label}</span></div>
      <div className="kpi-value" style={{ color: colors[tone] || "var(--text)" }}>{value}</div>
      <div className="kpi-foot">{foot}</div>
    </div>
  );
}

export { Dashboard };
